import React, { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import {
  buildRussianProductionCalendar,
  getRussianProductionCalendarDayDescription,
} from '../../utils/russianProductionCalendar';
import styles from './WorkCalendar.module.css';

const MIN_YEAR = 2024;
const MAX_YEAR = 2028;
const WEEKDAY_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const TYPE_LABELS = {
  working: 'Рабочий',
  weekend: 'Выходной',
  holiday: 'Праздник',
  short_day: 'Сокращённый',
};

function clampYear(value) {
  return Math.min(MAX_YEAR, Math.max(MIN_YEAR, value));
}

function toYmd(date) {
  return format(date, 'yyyy-MM-dd');
}

const WorkCalendar = () => {
  const today = new Date();
  const [year, setYear] = useState(clampYear(today.getFullYear()));
  const [currentDate, setCurrentDate] = useState(() => {
    const y = clampYear(today.getFullYear());
    return today.getFullYear() === y ? today : new Date(y, 0, 1);
  });
  const [selected, setSelected] = useState(() => toYmd(currentDate));

  const { days, stats, preliminary } = useMemo(
    () => buildRussianProductionCalendar(year),
    [year],
  );

  const daysByDate = useMemo(() => {
    const map = {};
    days.forEach((day) => {
      map[day.date] = day;
    });
    return map;
  }, [days]);

  const monthPrefix = format(currentDate, 'yyyy-MM');
  const monthEvents = days.filter(
    (d) =>
      d.date.startsWith(monthPrefix) &&
      (d.type === 'holiday' || d.type === 'short_day'),
  );
  const selectedDay = daysByDate[selected];

  const gridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const changeYear = (nextYear) => {
    const clamped = clampYear(nextYear);
    setYear(clamped);
    const next = new Date(currentDate);
    next.setFullYear(clamped);
    setCurrentDate(next);
    const nextSelected = new Date(`${selected}T00:00:00`);
    nextSelected.setFullYear(clamped);
    setSelected(toYmd(nextSelected));
  };

  const changeMonth = (delta) => {
    const next = addMonths(currentDate, delta);
    if (next.getFullYear() < MIN_YEAR || next.getFullYear() > MAX_YEAR) {
      return;
    }
    setCurrentDate(next);
    if (next.getFullYear() !== year) {
      setYear(next.getFullYear());
    }
    if (format(new Date(`${selected}T00:00:00`), 'yyyy-MM') !== format(next, 'yyyy-MM')) {
      setSelected(toYmd(startOfMonth(next)));
    }
  };

  return (
    <section className="main">
      <div className="content-wrapper">
        <div className={`page-title-container ${styles.pageTitle}`}>
          <h1 className="page-title">Производственный календарь</h1>
        </div>
        <p className={styles.subtitle}>
          {`${MIN_YEAR}–${MAX_YEAR} · Россия${preliminary ? ' · предварительно' : ''}`}
        </p>

        <div className={styles.layout}>
        <div className={styles.side}>
        <div className={styles.yearNav}>
          <button
            type="button"
            className={styles.yearBtn}
            disabled={year <= MIN_YEAR}
            onClick={() => changeYear(year - 1)}
          >
            ‹
          </button>
          <div className={styles.yearLabel}>{year}</div>
          <button
            type="button"
            className={styles.yearBtn}
            disabled={year >= MAX_YEAR}
            onClick={() => changeYear(year + 1)}
          >
            ›
          </button>
        </div>

        <div className={styles.monthNav}>
          <button type="button" className={styles.monthBtn} onClick={() => changeMonth(-1)}>
            {format(addMonths(currentDate, -1), 'LLL yyyy', { locale: ruLocale })}
          </button>
          <button type="button" className={styles.monthBtn} onClick={() => changeMonth(1)}>
            {format(addMonths(currentDate, 1), 'LLL yyyy', { locale: ruLocale })}
          </button>
        </div>
        </div>

        <div className={styles.calendar}>
        <div className={styles.monthTitle}>
          {format(currentDate, 'LLLL yyyy', { locale: ruLocale })}
        </div>
        <div className={styles.weekdays}>
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className={styles.weekday}>
              {label}
            </div>
          ))}
        </div>
        <div className={styles.grid}>
          {gridDays.map((date) => {
            const ymd = toYmd(date);
            const info = daysByDate[ymd];
            const inMonth = date.getMonth() === currentDate.getMonth();
            const isOff = info?.type === 'weekend' || info?.type === 'holiday';
            const isShort = info?.type === 'short_day';
            const isSelected = selected === ymd;
            const className = [
              styles.day,
              isOff ? styles.dayOff : '',
              isShort ? styles.dayShort : '',
              isSelected ? styles.daySelected : '',
              !inMonth ? styles.dayOutside : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={ymd}
                type="button"
                className={className}
                disabled={!inMonth}
                onClick={() => inMonth && setSelected(ymd)}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
        </div>
        </div>

        {selectedDay ? (
          <div className={styles.dayRow}>
            <div className={`${styles.dot} ${styles.dotActive}`} />
            <div>
              <div className={styles.rowTitle}>Выбранная дата</div>
              <div className={styles.rowType}>{TYPE_LABELS[selectedDay.type]}</div>
              <div className={styles.rowDate}>
                {format(new Date(`${selectedDay.date}T00:00:00`), 'dd MMMM yyyy', {
                  locale: ruLocale,
                })}
              </div>
              <div className={styles.rowDesc}>
                {getRussianProductionCalendarDayDescription(selectedDay)}
              </div>
            </div>
          </div>
        ) : null}

        {monthEvents.length > 0 ? (
          <>
            <div className={styles.section}>
              {`Праздники и переносы · ${format(currentDate, 'LLLL yyyy', {
                locale: ruLocale,
              })}`}
            </div>
            {monthEvents.map((day) => (
                <div
                  key={day.date}
                  className={styles.dayRow}
                >
                  <div className={styles.dot} />
                  <div>
                    <div className={styles.rowType}>{TYPE_LABELS[day.type]}</div>
                    <div className={styles.rowDate}>
                      {format(new Date(`${day.date}T00:00:00`), 'dd MMMM yyyy', {
                        locale: ruLocale,
                      })}
                    </div>
                    <div className={styles.rowDesc}>
                      {getRussianProductionCalendarDayDescription(day)}
                    </div>
                  </div>
                </div>
              ))}
          </>
        ) : null}

        {stats ? (
          <div className={styles.stats}>
            <div className={styles.section}>Итого за год</div>
            {Object.keys(TYPE_LABELS).map((key) => (
              <p key={key}>
                {TYPE_LABELS[key]}: {stats[key]}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default WorkCalendar;
